import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  fetchChatCardReactions,
  toggleChatCardReaction,
} from '../services/chatCommunityService'
import type { CommunityReaction } from '@/modules/comunidade/services/communityService'

type UseChatCardReactionsResult = {
  loading: boolean
  reactions: CommunityReaction[]
  toggleEmoji: (emoji: string) => Promise<void>
  reload: () => Promise<void>
}

export function useChatCardReactions(
  chatId: number | null,
  active: boolean,
): UseChatCardReactionsResult {
  const [loading, setLoading] = React.useState(false)
  const [reactions, setReactions] = React.useState<CommunityReaction[]>([])

  const load = React.useCallback(async () => {
    if (!chatId || !active) {
      setReactions([])
      return
    }

    setLoading(true)
    try {
      const data = await fetchChatCardReactions(chatId)
      setReactions(data)
    } catch {
      setReactions([])
    } finally {
      setLoading(false)
    }
  }, [active, chatId])

  React.useEffect(() => {
    if (!chatId || !active) {
      setReactions([])
      return
    }

    let alive = true
    let interval: any = null

    ;(async () => {
      await load()
      if (!alive) return
    })()

    const channel = supabase
      .channel(`chat_card:${chatId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_community_reactions',
          filter: `chat_id=eq.${chatId}`,
        } as any,
        () => {
          load().catch(() => {
            // ignorar: realtime/polling corrige
          })
        },
      )
      .subscribe()

    interval = setInterval(() => {
      load().catch(() => {
        // silencioso; proximo ciclo corrige
      })
    }, 3500)

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // noop
      }
      try {
        if (interval) clearInterval(interval)
      } catch {
        // noop
      }
      alive = false
    }
  }, [active, load, chatId])

  const toggleEmoji = React.useCallback(
    async (emoji: string) => {
      if (!chatId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setReactions((prev) => applyOptimisticReactionToggle(prev, trimmed))

      void toggleChatCardReaction(chatId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[Chat] toggleChatCardReaction error', error)
        load().catch(() => {
          // silencioso; proximo ciclo/realtime corrige
        })
      })
    },
    [active, chatId, load],
  )

  return {
    loading,
    reactions,
    toggleEmoji,
    reload: load,
  }
}
